// src/utils/optionFieldValidation.ts
// 拡張フィールドのバリデーション処理

import { OptionField } from '../services/option-field-api';

/**
 * 拡張フィールドのバリデーションエラー
 */
export interface OptionFieldValidationError {
  field: string;
  message: string;
}

/**
 * 拡張フィールドのバリデーション結果
 */
export interface OptionFieldValidationResult {
  isValid: boolean;
  errors: OptionFieldValidationError[];
}

/**
 * 拡張フィールドのバリデーション処理クラス
 */
export class OptionFieldValidator {
  
  /**
   * 必須入力チェック
   */
  static validateRequired(value: any, field: OptionField): boolean {
    let checkKindList = field.javascriptcheckKindList;
    
    // 文字列の場合はカンマ区切りで配列に変換
    if (typeof checkKindList === 'string') {
      checkKindList = (checkKindList as string).split(',');
    }
    
    if (!checkKindList?.includes('acs_valid_require_n') && 
        !checkKindList?.includes('acs_valid_require_o')) {
      return true;
    }
    
    if (value === null || value === undefined || value === '') {
      return false;
    }
    
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }
    
    return true;
  }

  /**
   * 文字数制限チェック
   */
  static validateCharacterLimit(value: any, field: OptionField): boolean {
    if (!field.javascriptcheckDigit || field.optionfieldtype !== 1) {
      return true;
    }
    
    const stringValue = String(value || '');
    return stringValue.length <= field.javascriptcheckDigit;
  }

  /**
   * 数値チェック
   */
  static validateNumeric(value: any, field: OptionField): boolean {
    let checkKindList = field.javascriptcheckKindList;
    if (typeof checkKindList === 'string') {
      checkKindList = (checkKindList as string).split(',');
    }
    if (!checkKindList?.includes('acs_valid_numeric')) {
      return true;
    }
    
    if (!value) return true; // 空の場合は有効
    
    return !isNaN(Number(value));
  }

  /**
   * 金額チェック
   */
  static validatePrice(value: any, field: OptionField): boolean {
    let checkKindList = field.javascriptcheckKindList;
    if (typeof checkKindList === 'string') {
      checkKindList = (checkKindList as string).split(',');
    }
    if (!checkKindList?.includes('acs_valid_price')) {
      return true;
    }
    
    if (!value) return true; // 空の場合は有効
    
    const cleanValue = String(value).replace(/[,]/g, '');
    return !isNaN(Number(cleanValue));
  }

  /**
   * 半角チェック
   */
  static validateHalfWidth(value: any, field: OptionField): boolean {
    let checkKindList = field.javascriptcheckKindList;
    if (typeof checkKindList === 'string') {
      checkKindList = (checkKindList as string).split(',');
    }
    if (!checkKindList?.includes('acs_valid_half')) {
      return true;
    }
    
    if (!value) return true; // 空の場合は有効
    
    const stringValue = String(value);
    return /^[\x00-\x7F]*$/.test(stringValue);
  }

  /**
   * メールアドレスチェック
   */
  static validateEmail(value: any, field: OptionField): boolean {
    let checkKindList = field.javascriptcheckKindList;
    if (typeof checkKindList === 'string') {
      checkKindList = (checkKindList as string).split(',');
    }
    if (!checkKindList?.includes('acs_valid_email')) {
      return true;
    }
    
    if (!value) return true; // 空の場合は有効
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(String(value));
  }

  /**
   * ダブルクォートチェック
   */
  static validateDoubleQuote(value: any, field: OptionField): boolean {
    let checkKindList = field.javascriptcheckKindList;
    if (typeof checkKindList === 'string') {
      checkKindList = (checkKindList as string).split(',');
    }
    if (!checkKindList?.includes('acs_valid_quotes')) {
      return true;
    }
    
    if (!value) return true; // 空の場合は有効
    
    const stringValue = String(value);
    return !stringValue.includes('"');
  }

  /**
   * 単一フィールドのバリデーション
   */
  static validateField(value: any, field: OptionField): OptionFieldValidationError[] {
    const errors: OptionFieldValidationError[] = [];
    
    // 必須チェック
    if (!this.validateRequired(value, field)) {
      errors.push({
        field: field.optionfieldkey,
        message: `${field.dispname}は必須入力です`
      });
    }
    
    // 文字数制限チェック
    if (!this.validateCharacterLimit(value, field)) {
      errors.push({
        field: field.optionfieldkey,
        message: `${field.dispname}は${field.javascriptcheckDigit}文字以内で入力してください`
      });
    }
    
    // 数値チェック
    if (!this.validateNumeric(value, field)) {
      errors.push({
        field: field.optionfieldkey,
        message: `${field.dispname}は半角数値で入力してください`
      });
    }
    
    // 金額チェック
    if (!this.validatePrice(value, field)) {
      errors.push({
        field: field.optionfieldkey,
        message: `${field.dispname}は数値で入力してください`
      });
    }
    
    // 半角チェック
    if (!this.validateHalfWidth(value, field)) {
      errors.push({
        field: field.optionfieldkey,
        message: `${field.dispname}は半角文字で入力してください`
      });
    }
    
    // メールアドレスチェック
    if (!this.validateEmail(value, field)) {
      errors.push({
        field: field.optionfieldkey,
        message: `${field.dispname}は正しいメールアドレス形式で入力してください`
      });
    }
    
    // ダブルクォートチェック
    if (!this.validateDoubleQuote(value, field)) {
      errors.push({
        field: field.optionfieldkey,
        message: `${field.dispname}にダブルクォート（"）は使用できません`
      });
    }
    
    return errors;
  }

  /**
   * 複数フィールドのバリデーション
   */
  static validateFields(values: Record<string, any>, fields: OptionField[]): OptionFieldValidationResult {
    const allErrors: OptionFieldValidationError[] = [];
    
    fields.forEach(field => {
      if (field.invalidflag) return; // 無効なフィールドはスキップ
      
      const value = values[field.optionfieldkey];
      const fieldErrors = this.validateField(value, field);
      allErrors.push(...fieldErrors);
    });
    
    return {
      isValid: allErrors.length === 0,
      errors: allErrors
    };
  }
}